import { ReactNode, useState } from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { ActivityRow, RowKind } from "../types";

const Root = styled(Paper)(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  overflow: "hidden",
}));

const Head = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  padding: theme.spacing(2, 2.5, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeadLine = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  minHeight: 30,
  flexWrap: "wrap",
}));

const Title = styled(Typography)(({ theme }) => ({ ...theme.typography.h2, color: theme.palette.text.primary }));

const Caption = styled(Typography)(({ theme }) => ({ fontSize: "0.75rem", color: theme.palette.text.secondary }));

const Scroll = styled(Box)(() => ({ maxHeight: 560, overflowY: "auto" }));

const Entry = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  padding: theme.spacing(0.5, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.background.default },
  "&:last-of-type": { borderBottom: "none" },
}));

const Line = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  flexWrap: "wrap",
  minWidth: 0,
  // Dense rows: chips sized to the text line so they don't set the row height.
  "& .MuiChip-root": { height: 18, fontSize: "0.6875rem" },
  "& .MuiChip-label": { padding: theme.spacing(0, 0.75) },
}));

const Time = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  fontVariantNumeric: "tabular-nums",
  minWidth: 56,
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  minWidth: 0,
  overflowWrap: "anywhere",
}));

const Detail = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  paddingLeft: 62,
  lineHeight: 1.35,
  overflowWrap: "anywhere",
}));

const Payload = styled("pre")(({ theme }) => ({
  margin: theme.spacing(0.5, 0, 0.5, 8.75),
  padding: theme.spacing(1.25),
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.72rem",
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  overflowX: "auto",
  maxHeight: 320,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
}));

const Empty = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(3, 2.5),
  fontSize: "0.8125rem",
  color: theme.palette.text.secondary,
}));

const KIND_LABEL: Record<RowKind, string> = {
  device: "device",
  event: "event",
  agent_run: "agent run",
};

/** Colour is reserved for status that means something: a failed run and a
 *  run still in flight are the states worth the ink. */
function statusColour(status: string | null): "default" | "warning" | "error" | "success" {
  if (status === "failed" || status === "blocked") return "error";
  if (status === "running") return "warning";
  if (status === "complete") return "success";
  return "default";
}

function clock(at: string): string {
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleTimeString([], { hour12: false });
}

function ActivityEntry(props: { row: ActivityRow }) {
  const [open, setOpen] = useState(false);
  const { row } = props;
  const body =
    row.text !== null || row.payload !== null
      ? [row.text, row.payload ? JSON.stringify(row.payload, null, 2) : null].filter(Boolean).join("\n\n")
      : null;
  const colour = statusColour(row.status);

  return (
    <Entry
      onClick={() => body !== null && setOpen(!open)}
      // Rows with a payload behave as buttons for keyboard and screen-reader users.
      {...(body !== null
        ? {
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpen(!open);
              }
            },
          }
        : {})}
    >
      <Line>
        <Time>{clock(row.at)}</Time>
        <Label>{row.label}</Label>
        <Chip size="small" variant="outlined" label={KIND_LABEL[row.kind]} />
        {row.status ? (
          <Chip size="small" color={colour} variant={colour === "default" ? "outlined" : "filled"} label={row.status} />
        ) : null}
        {row.state_version !== null ? <Chip size="small" variant="outlined" label={`v${row.state_version}`} /> : null}
      </Line>
      {row.detail ? <Detail>{row.detail}</Detail> : null}
      {open && body ? <Payload>{body}</Payload> : null}
    </Entry>
  );
}

type ActivityFeedProps = {
  title: string;
  caption: string;
  rows: ActivityRow[];
  action?: ReactNode;
  emptyText?: string;
};

export function ActivityFeed(props: ActivityFeedProps) {
  return (
    <Root>
      <Head>
        <HeadLine>
          <Title>{props.title}</Title>
          {props.action}
        </HeadLine>
        <Caption>{props.caption}</Caption>
      </Head>
      {props.rows.length === 0 ? (
        <Empty>{props.emptyText ?? "Nothing yet."}</Empty>
      ) : (
        <Scroll>
          {props.rows.map((row) => (
            <ActivityEntry key={row.id} row={row} />
          ))}
        </Scroll>
      )}
    </Root>
  );
}
